import { queryKeys } from './queryKeys';
import { useAppQueryPost } from './useAppQuery';

export type EnrolmentPreviewAction = 'new' | 'retake' | 'alreadyHasAccess';

export interface IEnrolmentPreviewPair {
  employeeId: string;
  courseId: string;
  studentName: string;
  courseTitle: string;
  action: EnrolmentPreviewAction;
  /** Catalogue: false only for 'alreadyHasAccess'. Own portal: always false (nothing is charged). */
  billable: boolean;
}

interface IEnrolmentPreviewResponse {
  pairs: IEnrolmentPreviewPair[];
}

interface Props {
  courseIds: string[];
  /** Own-portal (IntegrationCourse) ids. Send these or `courseIds`, never both. */
  integrationCourseIds?: string[];
  employeeIds: string[];
}

/**
 * What enrolling the selected students into the selected courses would do, per (student, course)
 * pair, so the dialog can price the selection honestly: a student who already has live access to a
 * course is not charged for by either enrol path, so counting them would overstate the total.
 *
 * Advisory only — the enrol and checkout endpoints apply the same rule themselves and are the
 * authority. POST because the selection can carry a hundred student ids.
 */
export const useGetEnrolmentPreview = ({
  courseIds,
  integrationCourseIds = [],
  employeeIds,
}: Props) => {
  // Sorted so [A,B] and [B,A] share one cache entry.
  const sortedCourseIds = [...courseIds].sort();
  const sortedIntegrationCourseIds = [...integrationCourseIds].sort();
  const sortedEmployeeIds = [...employeeIds].sort();

  return useAppQueryPost<IEnrolmentPreviewResponse>('/TrainingManager/enrolment-preview', {
    queryKey: queryKeys.enrolmentPreview.pairs({
      courseIds: sortedCourseIds,
      integrationCourseIds: sortedIntegrationCourseIds,
      employeeIds: sortedEmployeeIds,
    }),
    config: {
      // The server rejects a body carrying both lists, so send only the one in use.
      data: {
        ...(sortedIntegrationCourseIds.length > 0
          ? { integrationCourseIds: sortedIntegrationCourseIds }
          : { courseIds: sortedCourseIds }),
        employeeIds: sortedEmployeeIds,
      },
    },
    enabled:
      (sortedCourseIds.length > 0 || sortedIntegrationCourseIds.length > 0) &&
      sortedEmployeeIds.length > 0,
    // NOT keepPreviousData: the previous selection's pairs would price the current one. With no data
    // the total simply excludes nothing, which overstates rather than understates what will be
    // charged — the safe direction to be briefly wrong in.
    placeholderData: undefined,
  });
};
